import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import { Picture } from "@/components/wpl/image/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

export const HeroEnterpriseTakeoverTurbo = () => {
    const intl = useIntl();

    const ctaLink = useLocalizedHref("/enterprise/atlassian-williams-racing");

    const turboBodyText = intl.formatMessage({
        id: "HomePage.hero-turbo.body-text",
        defaultMessage:
            "Introducing Atlassian Williams Racing. Two teamwork powerhouses join forces to usher in a new era of Formula 1 racing."
    });

    const turboFeatureGate = checkFeatureFlagGate(
        featureFlags.gwp5046TurboBannerEnterprise
    );

    if (turboFeatureGate) {
        return (
            <UFOSegment name="homepage-turbo">
                <div css={heroTurboCSS}>
                    <div css={containingWidth}>
                        <div css={leftHero}>
                            <h2 className="hero hero-turbo">
                                <FormattedMessage
                                    id="HomePage.hero-turbo.heading"
                                    defaultMessage="Unleash the <strong>power of teamwork</strong>"
                                    values={DEFAULT_VALUES}
                                />
                            </h2>
                            <p className="turbo">{turboBodyText}</p>

                            <Adg4Button
                                variant="primary"
                                color="yellow"
                                size="large"
                                href={ctaLink}
                                dataEventProps={{
                                    "data-event": "clicked",
                                    "data-event-component": "linkButton",
                                    "data-event-container": "turboHero",
                                    "data-product-key": "home",
                                    "data-label-english": "Find out more"
                                }}
                            >
                                <FormattedMessage
                                    id="HomePage.hero-turbo.cta-link-livery"
                                    defaultMessage="Find out more"
                                />
                            </Adg4Button>
                        </div>
                        <div css={rightHero}>
                            <Picture
                                image={{
                                    alt: "",
                                    src: "https://dam-cdn.atl.orangelogic.com/AssetLink/4l06vv7x28fd2whw3f1i721165ooydn4.webp"
                                }}
                            />
                        </div>
                    </div>
                </div>
            </UFOSegment>
        );
    }
    return <></>;
};

const heroTurboCSS = css({
    overflow: "hidden",
    background: wacToken("color.extended.blue700"),
    margin: `0 0 ${token("space.600")}`,
    clipPath: "polygon(0 0, 100% 0, 100% 95%, 45% 100%, 45% 95%, 0 95%)"
});

const containingWidth = css({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "76px",
    width: "1200px",
    maxWidth: "100%",
    padding: `${token("space.800")} ${token("space.300")}`,
    margin: "auto",
    [media.below.md]: {
        flexDirection: "column",
        textAlign: "center",
        alignItems: "center",
        gap: token("space.200"),
        justifyContent: "center",
        padding: `${token("space.800")} 0 0`
    }
});

const leftHero = css({
    "position": "relative",
    "display": "flex",
    "gap": token("space.300"),
    "flexDirection": "column",
    "alignItems": "flex-start",
    "width": "48%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2.hero": {
        "fontWeight": "400",
        "fontSize": "2.75rem",
        "margin": 0,
        "lineHeight": "1.2",
        "color": wacToken("color.neutral.light0"),
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "strong": {
            fontWeight: "900",
            display: "block"
        },

        "&.hero-25": {
            [media.below.lg]: {
                fontSize: "2rem"
            }
        },
        "&.hero-turbo": {
            [media.below.md]: {
                fontSize: "2rem"
            }
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        "color": wacToken("color.neutral.light0"),
        "fontSize": "0.875rem",
        "margin": 0,
        "&.turbo": {
            fontSize: "1.25rem",
            lineHeight: 1.4,
            maxWidth: "500px",
            width: "100%"
        }
    },
    [media.below.md]: {
        width: "auto",
        maxWidth: "500px",
        padding: `0 ${token("space.300")}`,
        textAlign: "center",
        alignItems: "center",
        justifyContent: "center"
    }
});

const rightHero = css({
    position: "relative",
    padding: `${token("space.400")} 0`,
    width: "52%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    img: {
        width: "100%",
        height: "auto",
        aspectRatio: "555/313"
    },
    [media.below.md]: {
        width: "100%"
    }
});

HeroEnterpriseTakeoverTurbo.displayName = "HeroEnterpriseTakeoverTurbo";
